import type { DirectionCard, PaletteSystem, PreviewCopy } from "../domain.js";

type ColourOutput = DirectionCard["colour"];

export const HARMONY_ENGINE_VERSION = "color-please-direction/1.0.0";

const selectedPalette = (colour: ColourOutput): PaletteSystem =>
  colour.systems.find((system) => system.id === colour.recommendation.recommendedSystemId)
  ?? colour.systems[0]
  ?? (() => { throw new Error("A Color, Please direction needs at least one palette system."); })();

/** Compose a complete Color, Please result. There is deliberately no type slot,
 * compatibility score, or phantom dependency on another product. */
export const buildDirectionCard = (input: {
  projectId: string;
  name?: string;
  colour: ColourOutput;
  previewCopy?: PreviewCopy;
}): DirectionCard => {
  if (!input.projectId.trim()) throw new Error("Direction card needs a projectId.");
  const palette = selectedPalette(input.colour);
  const rulesFired: string[] = [];
  const guidance: string[] = [];
  const warnings: string[] = [];

  if (palette.strategy === "higher_contrast") {
    rulesFired.push("C20_HOSTILE_VIEWING");
    guidance.push("Treat contrast as infrastructure. Character can live in the accent; reading pairs stay exact and boring enough to work.");
  } else if (palette.strategy === "quieter") {
    rulesFired.push("C11_QUIET_WORLD");
    guidance.push("The field is calm. Let one source accent keep its nerve so the deck does not become beige competence.");
  } else {
    rulesFired.push("C12_SOURCE_CLOSE");
    guidance.push("The palette stays close to the source. Use the accent to interrupt; do not turn every role into a lead singer.");
  }

  const blockingWarnings = palette.warnings.filter((warning) => warning.severity === "blocking");
  if (blockingWarnings.length > 0) {
    rulesFired.push("C22_USE_BLOCKED");
    warnings.push(...blockingWarnings.map((warning) => warning.message));
  }
  if ((input.colour.companionSystems?.length ?? 0) > 0) {
    rulesFired.push("C23_COMPANION_MODE_READY");
    guidance.push("The opposite-mode companion was built and checked as its own system. It is not a lazy inversion wearing a moustache.");
  }

  return {
    schemaVersion: "1.0.0",
    product: "Color, Please",
    projectId: input.projectId,
    name: input.name?.trim() || "Untitled color direction",
    colour: input.colour,
    selected: { paletteSystemId: palette.id },
    relationship: {
      headline: `${palette.name} is ready.`,
      principle: "The image sets the temperature. Color, Please assigns jobs without changing source evidence or hiding the distance travelled.",
      rulesFired,
      guidance,
      warnings,
    },
    previewCopy: input.previewCopy ?? {},
    truth: {
      colourStatus: "engine_output",
      claims: [
        "Color output is deterministic from normalized pixels, declared answers, and engine version.",
        "Every role carries source or adjustment provenance.",
        "Light and dark companions are separately constructed and checked, not inverted.",
      ],
      limits: [
        "A palette cannot prove story quality, persuasion, cultural meaning, print fidelity, or universal accessibility.",
        "Final slides, charts, displays, projectors, and print processes still need human review.",
      ],
    },
  };
};
